import React, { useEffect, useState, useMemo } from 'react'
import {
  Box,
  Grid,
  TextField,
  Typography,
  FormControl,
  FormLabel,
  FormHelperText,
  FormControlLabel,
  Checkbox,
  Autocomplete,
  Chip,
  InputAdornment,
  Paper,
  Divider,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material'
import { ServicePackageFormData } from '../../types/servicePackage'
import { getFieldError } from '../../utils/servicePackageValidation'
import { servicePackageService } from '../../services/servicePackageService'
import api, { membershipApi } from '../../services/api'

interface Section1BasicsProps {
  formData: ServicePackageFormData
  onUpdate: (updates: Partial<ServicePackageFormData>) => void
  errors: any
  providerId: any
}

export const Section1Basics: React.FC<Section1BasicsProps> = ({
  formData,
  onUpdate,
  errors,
  providerId
}) => {
  const [specializations, setSpecializations] = useState<
    Array<{ id: string; name: string; description?: string }>
  >([])
  const [isLoadingSpecializations, setIsLoadingSpecializations] =
    useState(false)
  const [specializationError, setSpecializationError] = useState<string | null>(
    null
  )
  const [languages, setLanguages] = useState<
    Array<{ key: string; label: string }>
  >([])
  const [isLoadingLanguages, setIsLoadingLanguages] = useState(false)
  const [genders, setGenders] = useState<Array<{ key: string; label: string }>>(
    []
  )
  const [isLoadingGenders, setIsLoadingGenders] = useState(false)
  const [isLoadingLicenceOptions, setIsLoadingLicenceOptions] = useState(false)
  const [licenceOptions, setLicenceOptions] = useState<any>([])
  console.log('licenceOptions', licenceOptions)
  const selectedLicenseOption = useMemo(() => {
    const licenseId = formData.basics.license_id
    if (!licenseId || !licenceOptions || !licenceOptions.length) return null

    const normalizedId = String(licenseId).trim()
    return (
      licenceOptions.find(
        (opt: any) =>
          String(opt._id || opt.id).trim() === normalizedId
      ) || null
    )
  }, [licenceOptions, formData.basics.license_id])

  useEffect(() => {
    let isMounted = true

    const loadSpecializations = async () => {
      setIsLoadingSpecializations(true)
      try {
        const options = await servicePackageService.fetchSpecializations()
        if (isMounted) {
          setSpecializations(options)
          setSpecializationError(null)
        }
      } catch (error) {
        console.error('Failed to load specializations', error)
        if (isMounted) {
          setSpecializationError(
            'Unable to load specializations. Please try again later.'
          )
        }
      } finally {
        if (isMounted) {
          setIsLoadingSpecializations(false)
        }
      }
    }

    loadSpecializations()

    return () => {
      isMounted = false
    }
  }, [])

  // Fetch languages from API
  useEffect(() => {
    let isMounted = true

    const loadLanguages = async () => {
      setIsLoadingLanguages(true)
      try {
        const response = await membershipApi.getLanguages()
        if (isMounted && response?.success && Array.isArray(response.data)) {
          setLanguages(response.data)
        }
      } catch (error) {
        console.error('Failed to load languages', error)
      } finally {
        if (isMounted) {
          setIsLoadingLanguages(false)
        }
      }
    }

    loadLanguages()

    return () => {
      isMounted = false
    }
  }, [])

  // Fetch genders from API
  useEffect(() => {
    let isMounted = true

    const loadGenders = async () => {
      setIsLoadingGenders(true)
      try {
        const response = await membershipApi.getGenders()
        if (isMounted && response?.success && Array.isArray(response.data)) {
          setGenders(response.data)
        }
      } catch (error) {
        console.error('Failed to load genders', error)
      } finally {
        if (isMounted) {
          setIsLoadingGenders(false)
        }
      }
    }

    loadGenders()

    return () => {
      isMounted = false
    }
  }, [])

  const handleBasicsChange = (field: string, value: any) => {
    onUpdate({
      basics: {
        ...formData.basics,
        [field]: value,
      },
    })
  }

  const handleAgeRangeChange = (field: 'min' | 'max', value: string) => {
    // Allow empty input for clearing the field
    if (value === '') {
      onUpdate({
        basics: {
          ...formData.basics,
          ageRange: {
            ...formData.basics.ageRange,
            [field]: null,
          },
        },
      })
      return
    }
    // Parse the value
    const numValue = Number(value)
    // Reject if not a valid number or less than 1 (no ages 0 and under)
    if (isNaN(numValue) || numValue < 1) {
      return // Don't update if invalid
    }
    // Update with valid value (minimum 1)
    onUpdate({
      basics: {
        ...formData.basics,
        ageRange: {
          ...formData.basics.ageRange,
          [field]: numValue,
        },
      },
    })
  }

  const handleModalityChange = (value: string[]) => {
    handleBasicsChange('modality', value)
  }

  const handleLanguagesChange = (value: string[]) => {
    handleBasicsChange('languages', value)
  }

  const handleTargetGenderChange = (newValue: string[]) => {
    // Handle "All Genders" special case
    const allGendersOption = 'All Genders'
    const currentGenders = formData.basics.targetGender || []

    // Check if "All Genders" is in the new selection
    const allGendersSelected = newValue.includes(allGendersOption)
    const wasAllGendersSelected = currentGenders.includes(allGendersOption)

    if (allGendersSelected) {
      // If "All Genders" is selected (either newly or already), only keep "All Genders"
      // This handles both: selecting "All Genders" and trying to add other options when "All Genders" is already selected
      handleBasicsChange('targetGender', [allGendersOption])
    } else {
      // If "All Genders" is not selected, allow normal multi-select behavior
      handleBasicsChange('targetGender', newValue)
    }
  }

  const getError = (field: string) => getFieldError(errors, field)


  const getLicenceOptions = async () => {
    setIsLoadingLicenceOptions(true)
    try {
      const response = await api.get(`/therapist/license/all-licence/${providerId}`)
      // Handle different possible response structures
      const data = response?.data?.data || response?.data || []
      setLicenceOptions(Array.isArray(data) ? data : [])
    } catch (error) {
      console.error('Failed to load licence options', error)
    } finally {
      setIsLoadingLicenceOptions(false)
    }
  }

  useEffect(() => {
    getLicenceOptions()
  }, [])

  const handleLicenseChange = (value: any) => {
    handleBasicsChange('license_id', value?._id || value?.id || '')
  }






  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Basic Information
      </Typography>
      <Typography variant="body2" color="text.secondary" paragraph>
        Provide the core details about your service package. This information
        will be visible to potential clients.
      </Typography>

      <Grid container spacing={3}>
        {/* Job Title */}
        <Grid item xs={12}>
          <TextField
            id="service-title-field"
            fullWidth
            label="Service Title"
            variant="outlined"
            value={formData.basics.serviceTitle}
            onChange={e => handleBasicsChange('serviceTitle', e.target.value)}
            error={!!getError('serviceTitle')}
            helperText={
              getError('serviceTitle') ||
              'Enter a clear, descriptive title (5-80 characters)'
            }
            inputProps={{ maxLength: 80 }}
            required
          />
        </Grid>

        {/* Discipline */}
        <Grid item xs={12} sm={6}>
          <FormControl
            fullWidth
            error={!!getError('discipline')}
            id="discipline-field"
            required
          >
            <InputLabel id="discipline-label">Discipline</InputLabel>
            <Select
              labelId="discipline-label"
              id="discipline-select"
              value={formData.basics.discipline || ''}
              label="Discipline"
              onChange={e => handleBasicsChange('discipline', e.target.value)}
              disabled={isLoadingSpecializations}
              renderValue={selected => {
                if (!selected) return ''
                const option = specializations.find(s => s.id === selected)
                if (option) return option.name
                return isLoadingSpecializations ? 'Loading...' : ''
              }}
            >
              <MenuItem value="" disabled>
                {isLoadingSpecializations
                  ? 'Loading specializations...'
                  : 'Select discipline'}
              </MenuItem>
              {specializations.map(option => (
                <MenuItem key={option.id} value={option.id}>
                  {option.name}
                </MenuItem>
              ))}
            </Select>
            {specializationError && (
              <FormHelperText error>{specializationError}</FormHelperText>
            )}
            {getError('discipline') && (
              <FormHelperText>{getError('discipline')}</FormHelperText>
            )}
          </FormControl>
        </Grid>

        {/* licence dropdown */}
        <Grid item xs={12} sm={6}>
          <Autocomplete
            options={licenceOptions || []}
            getOptionLabel={option => {
              if (typeof option === 'string') return option;
              if (!option) return '';
              
              const npi = option.licenseNpi || option.npi;
              if (npi) return String(npi);
              
              // Fallbacks if NPI doesn't exist
              const number = option.licenseNumber || option.license_number || option.number;
              if (number) return String(number);
              
              const typeName = option.licenseTypeName || option.license_type || option.type;
              if (typeName) return String(typeName);
              
              return option.name || option.title || String(option._id || option.id || 'Unknown License');
            }}
            value={selectedLicenseOption}
            onChange={(_, value) => {
              handleLicenseChange(value)
            }}
            renderOption={(props, option) => {
              const typeName = option.licenseTypeName || option.license_type || option.type || 'Unknown Type';
              const number = option.licenseNumber || option.license_number || option.number || 'N/A';
              const npi = option.licenseNpi || option.npi || 'N/A';
              
              return (
                <Box component="li" {...props} key={option._id || option.id}>
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {typeName}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      <span style={{ fontWeight: "bold" }}>License Number: </span>{number}, <span style={{ fontWeight: "bold" }}>NPI: </span> {npi}
                    </Typography>
                  </Box>
                </Box>
              )
            }}
            renderInput={params => (
              <TextField
                {...params}
                label="License/NPI"
                placeholder="Search licenses..."
                required
                error={!!getError('license_id')}
                helperText={getError('license_id')}
              />
            )}
            loading={isLoadingLicenceOptions}
            isOptionEqualToValue={(option, value) => {
              if (!option || !value) return false
              return String(option._id || option.id) === String(value._id || value.id)
            }}
          />
        </Grid>







        {/* Modality */}
        <Grid item xs={12} sm={6}>
          <FormControl fullWidth id="modality-field">
            <FormLabel id="modality-label" required>
              Service Modality
            </FormLabel>
            <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
              <FormControlLabel
                control={<Checkbox checked disabled />}
                label={
                  <Box>
                    <Typography variant="body1">In-Person</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Service sessions conducted at the patient's location
                    </Typography>
                  </Box>
                }
              />
            </Box>
          </FormControl>
        </Grid>

        {/* Short Description */}
        <Grid item xs={12}>
          <TextField
            id="short-description-field"
            fullWidth
            label="Short Description"
            variant="outlined"
            value={formData.basics.shortDescription}
            onChange={e =>
              handleBasicsChange('shortDescription', e.target.value)
            }
            error={!!getError('shortDescription')}
            helperText={
              getError('shortDescription') ||
              `Brief overview of your service (${formData.basics.shortDescription.length}/300 characters, min 40)`
            }
            multiline
            rows={2}
            inputProps={{ maxLength: 300 }}
            required
          />
        </Grid>

        {/* Detailed Description */}
        <Grid item xs={12}>
          <TextField
            id="detailed-description-field"
            fullWidth
            label="Detailed Description"
            variant="outlined"
            value={formData.basics.detailedDescription}
            onChange={e =>
              handleBasicsChange('detailedDescription', e.target.value)
            }
            error={!!getError('detailedDescription')}
            helperText={
              getError('detailedDescription') ||
              `Comprehensive description of your service (${formData.basics.detailedDescription.length}/5000 characters)`
            }
            multiline
            rows={4}
            inputProps={{ maxLength: 5000 }}
          />
        </Grid>

        <Grid item xs={12}>
          <Divider sx={{ my: 2 }} />
          <Typography variant="subtitle1" gutterBottom>
            Additional Information
          </Typography>
        </Grid>

        {/* Age Range */}
        <Grid item xs={12} sm={6}>
          <Paper variant="outlined" sx={{ p: 2 }} id="age-range-field">
            <Typography variant="subtitle2" gutterBottom>
              Age Range (Optional)
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TextField
                  id="age-min-field"
                  fullWidth
                  label="Minimum Age"
                  type="number"
                  variant="outlined"
                  value={
                    formData.basics.ageRange.min === null
                      ? ''
                      : formData.basics.ageRange.min
                  }
                  onChange={e => handleAgeRangeChange('min', e.target.value)}
                  onBlur={e => {
                    // On blur, ensure value is valid or clear it
                    const numValue = Number(e.target.value)
                    if (isNaN(numValue) || numValue < 1) {
                      onUpdate({
                        basics: {
                          ...formData.basics,
                          ageRange: {
                            ...formData.basics.ageRange,
                            min: null,
                          },
                        },
                      })
                    }
                  }}
                  error={!!getError('ageRange.min')}
                  helperText={
                    getError('ageRange.min') || 'Minimum age must be 1 or older'
                  }
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">years</InputAdornment>
                    ),
                  }}
                  inputProps={{ min: 1, step: 1 }}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  id="age-max-field"
                  fullWidth
                  label="Maximum Age"
                  type="number"
                  variant="outlined"
                  value={
                    formData.basics.ageRange.max === null
                      ? ''
                      : formData.basics.ageRange.max
                  }
                  onChange={e => handleAgeRangeChange('max', e.target.value)}
                  onBlur={e => {
                    // On blur, ensure value is valid or clear it
                    const numValue = Number(e.target.value)
                    if (isNaN(numValue) || numValue < 1) {
                      onUpdate({
                        basics: {
                          ...formData.basics,
                          ageRange: {
                            ...formData.basics.ageRange,
                            max: null,
                          },
                        },
                      })
                    }
                  }}
                  error={!!getError('ageRange.max')}
                  helperText={
                    getError('ageRange.max') || 'Maximum age must be 1 or older'
                  }
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">years</InputAdornment>
                    ),
                  }}
                  inputProps={{ min: 1, step: 1 }}
                />
              </Grid>
            </Grid>
            {getError('ageRange') && (
              <FormHelperText error>{getError('ageRange')}</FormHelperText>
            )}
          </Paper>
        </Grid>

        {/* Languages */}
        <Grid item xs={12} sm={6}>
          <Paper variant="outlined" sx={{ p: 2 }} id="languages-field">
            <Typography variant="subtitle2" gutterBottom>
              Languages (Optional)
            </Typography>
            <Autocomplete
              multiple
              id="languages"
              options={languages.map(lang => lang.label)}
              value={formData.basics.languages}
              onChange={(_, newValue) => handleLanguagesChange(newValue)}
              loading={isLoadingLanguages}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    label={option}
                    size="small"
                    {...getTagProps({ index })}
                  />
                ))
              }
              renderInput={params => (
                <TextField
                  {...params}
                  variant="outlined"
                  label="Select Languages"
                  placeholder={
                    isLoadingLanguages ? 'Loading...' : 'Add language'
                  }
                  helperText="Languages you can provide service in"
                />
              )}
            />
          </Paper>
        </Grid>

        {/* Target Gender */}
        <Grid item xs={12} sm={6}>
          <Paper variant="outlined" sx={{ p: 2 }} id="target-gender-field">
            <Typography variant="subtitle2" gutterBottom>
              Target Gender (Optional)
            </Typography>
            <Autocomplete
              multiple
              id="target-gender"
              options={genders.map(gender => gender.label)}
              value={formData.basics.targetGender || []}
              onChange={(_, newValue) => handleTargetGenderChange(newValue)}
              loading={isLoadingGenders}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    label={option}
                    size="small"
                    {...getTagProps({ index })}
                  />
                ))
              }
              renderInput={params => (
                <TextField
                  {...params}
                  variant="outlined"
                  label="Select Target Gender"
                  placeholder={isLoadingGenders ? 'Loading...' : 'Add gender'}
                  helperText="Identify the primary audience for this service (optional)"
                />
              )}
            />
          </Paper>
        </Grid>
      </Grid>
    </Box>
  )
}

export default Section1Basics
